// src/components/admin/plans/StudentPlansPage.js
//
// "Plans" hub — every study plan across all students in one searchable table,
// modeled on the unified Diagnostic Reports page (DiagnosticReports.js). Custom
// plans and SAT/ACT diagnostic-generated plans are listed together.
//
// Clicking a row opens that student's Plan tab focused on the plan (a new tab),
// which is also where teachers can create/edit plans. Soft-deleted ("pending
// deletion") plans are excluded — those are an admin-only concern surfaced in the
// student's Plan tab for restore / permanent delete.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchAllPlans,
  fetchStudentsLite,
  summarizePlanProgress,
  derivePlanDisplayStatus,
  isPendingDeletion,
  deletionDaysLeft,
  restorePlan,
  requestPlanDeletion,
  deletePlan,
  purgeExpiredPlanDeletions,
  tsToMs,
  DELETION_WINDOW_DAYS,
} from "../../../utils/sat/satPlanData";
import { planTypeOf, PLAN_TYPES, isCustomPlan } from "../../../utils/plan/planConfig";
import { openPlanEditor } from "../../../utils/plan/planLinks";
import { ADMIN_PERMISSIONS, TUTOR_PERMISSIONS } from "../student/dashboard/permissions";
import { auth } from "../../../firebaseConfig";
import ProgressGauge from "../../sat/ProgressGauge";
import { Pagination } from "../../session_logs/ProgressReportWorkspace";

import "../../session_logs/ProgressReportWorkspace.css";
import "./studentPlansPage.css";

const PLANS_PER_PAGE = 20;

const TYPE_PILL = {
  SAT: { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
  ACT: { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" },
  custom: { bg: "#f5f3ff", color: "#6d28d9", border: "#ddd6fe" },
};

const STATUS_BADGE = {
  active: { label: "Active", bg: "#dcfce7", color: "#166534" },
  completed: { label: "Completed", bg: "#dbeafe", color: "#1e40af" },
  draft: { label: "Draft", bg: "#f1f5f9", color: "#475569" },
  archived: { label: "Archived", bg: "#f1f5f9", color: "#94a3b8" },
};

const miniBtn = {
  border: "1px solid #e5e7eb",
  background: "#fff",
  color: "#334155",
  borderRadius: 6,
  padding: "3px 9px",
  fontSize: 11.5,
  fontWeight: 600,
  cursor: "pointer",
};

function TypePill({ type }) {
  const c = TYPE_PILL[type] || TYPE_PILL.custom;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 8px",
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.3px",
        background: c.bg,
        color: c.color,
        border: `1px solid ${c.border}`,
        lineHeight: 1.4,
        whiteSpace: "nowrap",
      }}
    >
      {PLAN_TYPES[type]?.label || type}
    </span>
  );
}

function StatusBadge({ plan }) {
  if (isPendingDeletion(plan)) {
    return (
      <span
        style={{
          display: "inline-flex",
          padding: "2px 8px",
          borderRadius: 99,
          fontSize: 11,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          background: "#fef2f2",
          color: "#b91c1c",
          whiteSpace: "nowrap",
        }}
      >
        Deletion · {deletionDaysLeft(plan)}d left
      </span>
    );
  }
  const b = STATUS_BADGE[derivePlanDisplayStatus(plan)] || STATUS_BADGE.draft;
  return (
    <span
      style={{
        display: "inline-flex",
        padding: "2px 8px",
        borderRadius: 99,
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        background: b.bg,
        color: b.color,
        whiteSpace: "nowrap",
      }}
    >
      {b.label}
    </span>
  );
}

/** Row status used by the status filter (pending deletion is its own state). */
function rowStatus(plan) {
  return isPendingDeletion(plan) ? "pending" : derivePlanDisplayStatus(plan);
}

function formatDate(ms) {
  if (!ms) return "—";
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
}

function planLabel(plan) {
  const type = planTypeOf(plan);
  if (isCustomPlan(plan)) return plan.title || "Custom plan";
  return plan.attemptLabel || `${PLAN_TYPES[type].label} diagnostic`;
}

/** Profile URL that opens a student's Plan tab focused on this plan. */
function planUrl(plan) {
  const dir = plan?.studentDirId;
  if (!dir) return null;
  const q = plan?.id ? `?plan=${encodeURIComponent(plan.id)}` : "";
  return `/students/directory/${encodeURIComponent(dir)}/plan${q}`;
}

export default function StudentPlansPage({ viewerRole = "tutor" }) {
  // Admins additionally see soft-deleted ("pending deletion") plans so they can
  // restore them or delete them permanently within the 30-day window.
  const isAdmin = viewerRole === "admin";
  const colCount = isAdmin ? 8 : 7;
  // Reuse the same permission sets the student dashboard tabs use so the Plans
  // hub gates its actions identically (admins may hard-delete, teachers may
  // create/edit and request deletion).
  const perms = isAdmin ? ADMIN_PERMISSIONS : TUTOR_PERMISSIONS;
  const navigate = useNavigate();

  const [plans, setPlans] = useState([]);
  // studentDirId -> name, and diagnosticUid -> name (fallback for legacy plans).
  const [nameIndex, setNameIndex] = useState({ byId: {}, byDiag: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  // Right-click context menu state: { x, y, plan }.
  const [rowMenu, setRowMenu] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        // Admins also load soft-deleted ("pending deletion") plans so they can be
        // restored or permanently deleted; tutors never see them.
        const [all, students] = await Promise.all([
          fetchAllPlans({ includePending: isAdmin }),
          fetchStudentsLite(),
        ]);
        if (cancelled) return;
        const names = {};
        const byDiagnosticUid = {};
        (students || []).forEach((s) => {
          if (s.name) names[s.id] = s.name;
          if (s.diagnosticUid) byDiagnosticUid[s.diagnosticUid] = s.name;
        });
        let list = all || [];
        // Lazy sweep: permanently drop plans whose 30-day restore window has
        // elapsed. Firestore deletes are admin-only, so only admins run this.
        if (isAdmin && list.length) {
          const purged = await purgeExpiredPlanDeletions(list);
          if (purged) list = list.filter((p) => !isPendingDeletion(p) || deletionDaysLeft(p) > 0);
        }
        if (cancelled) return;
        setNameIndex({ byId: names, byDiag: byDiagnosticUid });
        setPlans(list);
      } catch (e) {
        if (!cancelled) setError(`Failed to load plans: ${e?.message || e}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  useEffect(() => {
    setPage(1);
  }, [search, typeFilter, statusFilter]);

  const studentNameOf = useMemo(
    () => (plan) =>
      plan.studentName ||
      nameIndex.byId[plan.studentDirId] ||
      nameIndex.byDiag[plan.diagnosticStudentId] ||
      "—",
    [nameIndex]
  );

  // Tutors never see pending-deletion plans; admins do.
  const visible = useMemo(
    () => (isAdmin ? plans : plans.filter((p) => !isPendingDeletion(p))),
    [plans, isAdmin]
  );

  const availableTypes = useMemo(() => {
    const set = new Set(visible.map((p) => planTypeOf(p)));
    return ["SAT", "ACT", "custom"].filter((t) => set.has(t));
  }, [visible]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return visible.filter((p) => {
      const type = planTypeOf(p);
      if (typeFilter !== "all" && type !== typeFilter) return false;
      if (statusFilter !== "all" && rowStatus(p) !== statusFilter) return false;
      if (!q) return true;
      const haystack = [
        studentNameOf(p),
        planLabel(p),
        type,
        ...(p.subjects || []),
        ...(p.checklist || []).slice(0, 40).map((i) => i.subject),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [visible, search, typeFilter, statusFilter, studentNameOf]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PLANS_PER_PAGE));
  const paged = filtered.slice((page - 1) * PLANS_PER_PAGE, page * PLANS_PER_PAGE);

  // Row click / "Open in new tab" both land on the student's profile with the
  // Plan tab active and this plan open. "Open" stays in the current tab.
  const openPlan = (plan) => {
    const url = planUrl(plan);
    if (url) window.open(url, "_blank", "noopener");
  };

  const openPlanSameTab = (plan) => {
    const url = planUrl(plan);
    if (url) navigate(url);
  };

  const openRowMenu = (e, plan) => {
    e.preventDefault();
    setRowMenu({ x: e.clientX, y: e.clientY, plan });
  };

  // Close the context menu on any outside click / scroll.
  useEffect(() => {
    if (!rowMenu) return undefined;
    const close = () => setRowMenu(null);
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [rowMenu]);

  const handleRestore = async (plan) => {
    if (!window.confirm(`Restore "${planLabel(plan)}"? It will be visible to tutors again.`)) return;
    try {
      await restorePlan(plan.id);
      setPlans((prev) =>
        prev.map((p) =>
          p.id === plan.id
            ? { ...p, deletionRequestedAt: null, deletionRequestedByUid: null, deletionRequestedByName: null }
            : p
        )
      );
    } catch (e) {
      alert(`Failed to restore: ${e?.message || e}`);
    }
  };

  const handlePurge = async (plan) => {
    if (!window.confirm(`Permanently delete "${planLabel(plan)}"?\n\nThis cannot be undone.`)) return;
    try {
      await deletePlan(plan.id);
      setPlans((prev) => prev.filter((p) => p.id !== plan.id));
    } catch (e) {
      alert(`Failed to delete: ${e?.message || e}`);
    }
  };

  const handleRequestDeletion = async (plan) => {
    const ok = window.confirm(
      `Delete "${planLabel(plan)}"?\n\n` +
        "It will be hidden from tutors right away and marked for deletion. " +
        (isAdmin
          ? `You can restore it or delete it permanently from this menu within ${DELETION_WINDOW_DAYS} days.`
          : `An admin can restore it within ${DELETION_WINDOW_DAYS} days, after which it is permanently deleted.`)
    );
    if (!ok) return;
    try {
      const user = auth.currentUser;
      await requestPlanDeletion(plan.id, {
        uid: user?.uid || null,
        name: user?.displayName || user?.email || null,
      });
      setPlans((prev) =>
        prev.map((p) => (p.id === plan.id ? { ...p, deletionRequestedAt: Date.now() } : p))
      );
    } catch (e) {
      alert(`Failed to request deletion: ${e?.message || e}`);
    }
  };

  const canEditPlan = (plan) =>
    !isPendingDeletion(plan) && (isCustomPlan(plan) ? perms.managePlans : perms.manageDeletedPlans);

  // Same shape as the Diagnostic Reports row menu, plus Edit.
  const buildMenuItems = (plan) => {
    const items = [
      { label: "Open", onClick: () => openPlanSameTab(plan) },
      { label: "Open in new tab", onClick: () => openPlan(plan) },
    ];
    if (isPendingDeletion(plan)) {
      if (perms.manageDeletedPlans) {
        items.push({ label: "Restore", onClick: () => handleRestore(plan) });
        items.push({ label: "Delete permanently", danger: true, onClick: () => handlePurge(plan) });
      }
      return items;
    }
    if (canEditPlan(plan)) {
      items.push({ label: "Edit", onClick: () => openPlanEditor(plan) });
    }
    // Deletion is deliberately two-step: soft-delete first (hidden from tutors),
    // then a second right-click on the pending row offers Restore / Delete
    // permanently. Admins never hard-delete a live plan directly.
    if (perms.requestPlanDeletion) {
      items.push({
        label: perms.manageDeletedPlans ? "Delete plan" : "Request deletion",
        danger: true,
        onClick: () => handleRequestDeletion(plan),
      });
    }
    return items;
  };

  const metaText = loading
    ? "Loading…"
    : `${filtered.length} plan${filtered.length !== 1 ? "s" : ""} — click a row to open the student's profile in a new tab`;

  return (
    <div className="prw-shell">
      <div className="prw-pageHeader">
        <h1>Plans</h1>
        <p>
          Browse, search, and filter every study plan created for a student — SAT, ACT, and
          custom plans for any subject. Click any row to open that student's profile on its
          Plan tab in a new tab.
        </p>
      </div>

      {/* ── Filter Card ── */}
      <div className="prw-card">
        <div className="prw-cardTitle">Filter Plans</div>
        <div className="prw-filterRow planshub-filterRow">
          <div className="prw-fieldGroup">
            <div className="prw-label">Search</div>
            <input
              type="text"
              className="prw-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Student, plan name, or subject…"
              disabled={loading}
            />
          </div>

          <div className="prw-fieldGroup">
            <div className="prw-label">Plan Type</div>
            <select
              className="prw-input"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              disabled={loading}
            >
              <option value="all">All types</option>
              {availableTypes.map((t) => (
                <option key={t} value={t}>
                  {PLAN_TYPES[t]?.label || t}
                </option>
              ))}
            </select>
          </div>

          <div className="prw-fieldGroup">
            <div className="prw-label">Status</div>
            <select
              className="prw-input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              disabled={loading}
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="completed">Completed</option>
              <option value="archived">Archived</option>
              {isAdmin && <option value="pending">Pending deletion</option>}
            </select>
          </div>
        </div>
      </div>

      {/* ── Plans Table ── */}
      <div className="prw-card">
        <div className="prw-sectionHeader">
          <div className="prw-sectionTitle">Plans</div>
          <div className="prw-sectionMeta">{metaText}</div>
        </div>

        {error && (
          <div className="prw-errorBox" style={{ marginBottom: 12 }}>
            {error}
          </div>
        )}

        <div className="prw-tableWrap">
          <table className="prw-table">
            <thead>
              <tr>
                <th>Created</th>
                <th>Student</th>
                <th>Plan</th>
                <th>Type</th>
                <th>Status</th>
                <th>Hours</th>
                <th>Progress</th>
                {isAdmin && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr className="prw-emptyRow">
                  <td colSpan={colCount}>Loading plans…</td>
                </tr>
              )}
              {!loading && paged.length === 0 && (
                <tr className="prw-emptyRow">
                  <td colSpan={colCount}>No plans found for the selected filters.</td>
                </tr>
              )}
              {!loading &&
                paged.map((plan) => {
                  const type = planTypeOf(plan);
                  const prog = summarizePlanProgress(plan);
                  return (
                    <tr
                      key={plan.id}
                      className="prw-clickableRow"
                      onClick={() => openPlan(plan)}
                      onContextMenu={(e) => openRowMenu(e, plan)}
                    >
                      <td>{formatDate(tsToMs(plan.confirmedAt || plan.createdAt))}</td>
                      <td style={{ fontWeight: 600 }}>{studentNameOf(plan)}</td>
                      <td>{planLabel(plan)}</td>
                      <td>
                        <TypePill type={type} />
                      </td>
                      <td>
                        <StatusBadge plan={plan} />
                      </td>
                      <td>{plan.totalHours || 0}h</td>
                      <td>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                          <ProgressGauge percent={prog.percent} size={26} stroke={3.5} showLabel={false} />
                          <span style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>
                            {prog.percent}%
                          </span>
                          <span style={{ fontSize: 11.5, color: "#94a3b8" }}>
                            {prog.doneCount}/{prog.requiredCount}
                          </span>
                        </span>
                      </td>
                      {isAdmin && (
                        <td onClick={(e) => e.stopPropagation()}>
                          {isPendingDeletion(plan) ? (
                            <span style={{ display: "inline-flex", gap: 8 }}>
                              <button type="button" style={miniBtn} onClick={() => handleRestore(plan)}>
                                Restore
                              </button>
                              <button
                                type="button"
                                style={{ ...miniBtn, color: "#b91c1c", borderColor: "#fecaca" }}
                                onClick={() => handlePurge(plan)}
                              >
                                Delete
                              </button>
                            </span>
                          ) : (
                            <span style={{ color: "#cbd5e1" }}>—</span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        <Pagination page={page} totalPages={totalPages} onPage={setPage} />

        <div className="prw-sectionMeta" style={{ marginTop: 8 }}>
          Tip: right-click a plan for more actions (open, open in new tab, edit, delete). Deletion is
          soft — a deleted plan is hidden from tutors and can be restored
          {isAdmin ? " or permanently deleted" : " by an admin"} from this menu.
        </div>
      </div>

      {/* ── Right-click context menu ── */}
      {rowMenu && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "fixed",
            top: rowMenu.y,
            left: rowMenu.x,
            zIndex: 9999,
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 10,
            boxShadow: "0 12px 30px rgba(15,23,42,0.18)",
            padding: 5,
            minWidth: 210,
          }}
        >
          {buildMenuItems(rowMenu.plan).map((it, i) => (
            <button
              key={i}
              onClick={() => {
                const fn = it.onClick;
                setRowMenu(null);
                fn();
              }}
              style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                border: "none",
                background: "none",
                textAlign: "left",
                fontSize: 13,
                fontWeight: it.danger ? 600 : 500,
                color: it.danger ? "#dc2626" : "#0f172a",
                padding: "8px 9px",
                borderRadius: 7,
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = it.danger ? "#fef2f2" : "#f1f5f9";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "none";
              }}
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
